import { useId, type ReactNode } from 'react';
import { iniciales } from './uso';
import { Boton } from '../../componentes/Boton';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { useAvisoBreve } from '../../componentes/avisoBreve';

/**
 * Piezas pequeñas de «Listas y RRPP» (10.9 a 10.11) dentro de sus RV Panel: filas de dato, cajas de aviso, bloques con
 * título, la botonera de las fichas, el contador de acompañantes, la fecha con hora y la tarjeta de un enlace con «Copiar».
 */




export function Dato({ icono, k, v, sub, aviso = false, children }: { icono?: NombreIcono; k: string; v: ReactNode; sub?: string; aviso?: boolean; children?: ReactNode }) {
    return (
        <div className="rv-ca-dato">
            <span className="rv-ls-dato-k">
                {icono !== undefined && <Icono nombre={icono} />}
                {k}
            </span>
            <span className="rv-ls-dato-v">
                <strong className={aviso ? 'rv-ca-aviso-texto' : undefined}>{v}</strong>
                {sub !== undefined && sub !== '' && <small>{sub}</small>}
                {children}
            </span>
        </div>
    );
}

export function Caja({ icono = 'info', tono = 'normal', titulo, children }: { icono?: NombreIcono; tono?: 'normal' | 'aviso' | 'error'; titulo?: string; children: ReactNode }) {
    return (
        <div className={`rv-ca-caja${tono === 'normal' ? '' : ` rv-ca-caja--${tono}`}`}>
            <div className="rv-ca-caja__fila">
                <Icono nombre={icono} />
                <span>
                    {titulo !== undefined && <strong>{titulo} </strong>}
                    {children}
                </span>
            </div>
        </div>
    );
}

export function Bloque({ titulo, intro, derecha, children }: { titulo?: string; intro?: string; derecha?: string; children: ReactNode }) {
    return (
        <section className="rv-ca-bloque">
            {(titulo !== undefined || intro !== undefined) && (
                <div className="rv-ls-bloque-cab">
                    <div className="rv-ca-bloque__cab">
                        {titulo !== undefined && <strong>{titulo}</strong>}
                        {intro !== undefined && <span>{intro}</span>}
                    </div>
                    {derecha !== undefined && <small>{derecha}</small>}
                </div>
            )}
            {children}
        </section>
    );
}

export function Botonera({ etiqueta, items, valor, onCambio }: { etiqueta: string; items: readonly { texto: string; icono: NombreIcono }[]; valor: number; onCambio: (i: number) => void }) {
    return (
        <div className="rv-ca-botonera" role="tablist" aria-label={etiqueta}>
            {items.map((x, i) => (
                <button key={x.texto} type="button" role="tab" aria-selected={valor === i} className="rv-ca-botonera__b" onClick={() => { onCambio(i); }}>
                    <Icono nombre={x.icono} />
                    {x.texto}
                </button>
            ))}
        </div>
    );
}

export function Botones({ nota, children }: { nota?: string; children: ReactNode }) {
    return (
        <div className="rv-ls-botones">
            <div className="rv-ls-botones__fila">{children}</div>
            {nota !== undefined && nota !== '' && <p className="rv-ca-nota rv-ca-nota--gris">{nota}</p>}
        </div>
    );
}

/** Fecha y hora (`datetime-local`) o solo hora (`time`), con la caja de RV Campo. */
export function FechaHora({ etiqueta, valor, onChange, soloHora = false, error, ayuda, max }: { etiqueta: string; valor: string; onChange: (v: string) => void; soloHora?: boolean; error?: string | null; ayuda?: string; max?: string }) {
    const idCampo = useId();
    const hayError = error != null && error !== '';
    return (
        <div className="rv-campo">
            <div className="rv-campo__fila">
                <label htmlFor={idCampo} className={`rv-campo__caja rv-campo__caja--fecha${hayError ? ' rv-campo__caja--error' : ''}`}>
                    <Icono nombre={soloHora ? 'clock' : 'calendar'} className="rv-campo__calendario" />
                    <span className="rv-campo__etiqueta rv-campo__etiqueta--arriba">{etiqueta}</span>
                    <input id={idCampo} type={soloHora ? 'time' : 'datetime-local'} value={valor} max={max} aria-invalid={hayError} onChange={(e) => { onChange(e.target.value); }} />
                </label>
            </div>
            {hayError && (
                <span role="alert" className="rv-campo__error">
                    <Icono nombre="circle-alert" />
                    <span>{error}</span>
                </span>
            )}
            {!hayError && ayuda !== undefined && <span className="rv-campo__ayuda">{ayuda}</span>}
        </div>
    );
}



export function Copiar({ texto, etiqueta }: { texto: string; etiqueta: string }) {
    const avisar = useAvisoBreve();
    return (
        <Boton
            tipo="secundario"
            icono="copy"
            aria-label={etiqueta}
            onClick={() => {
                const fallo = () => { avisar({ tipo: 'error', texto: 'No se ha podido copiar.' }); };
                try {
                    navigator.clipboard.writeText(texto).then(() => { avisar({ tipo: 'ok', texto: 'Copiado.' }); }, fallo);
                } catch {
                    fallo();
                }
            }}
        >
            Copiar
        </Boton>
    );
}

export function TarjetaEnlace({ titulo, texto, chip, icono = 'instagram', filas, children, apagada = false }: { titulo: string; texto?: string; chip?: [string, EstadoChip]; icono?: NombreIcono; filas: readonly { k: string; v: string; copiar?: boolean }[]; children?: ReactNode; apagada?: boolean }) {
    return (
        <div className={`rv-ls-enlace${apagada ? ' rv-ls-enlace--apagado' : ''}`}>
            <div className="rv-ls-enlace__cab">
                <span className="rv-ls-enlace__icono"><Icono nombre={icono} /></span>
                <span className="rv-ca-fila__texto">
                    <strong>
                        {titulo}
                        {chip !== undefined && <Chip estado={chip[1]}>{chip[0]}</Chip>}
                    </strong>
                    {texto !== undefined && <span>{texto}</span>}
                </span>
            </div>
            {filas.map((f) => (
                <div key={f.k} className="rv-ls-enlace__fila">
                    <span>
                        <small>{f.k}</small>
                        <code className={apagada ? 'rv-ls-tachado' : undefined}>{f.v}</code>
                    </span>
                    {f.copiar !== false && <Copiar texto={f.v.startsWith('http') ? f.v : `https://${f.v}`} etiqueta={`Copiar ${f.k}`} />}
                </div>
            ))}
            {children}
        </div>
    );
}

export function Vacia({ icono, titulo, texto, children }: { icono: NombreIcono; titulo: string; texto?: string; children?: ReactNode }) {
    return (
        <div className="rv-ca-vacia">
            <span className="rv-ca-vacia__baldosa"><Icono nombre={icono} /></span>
            <strong>{titulo}</strong>
            {texto !== undefined && texto !== '' && <span>{texto}</span>}
            {children}
        </div>
    );
}

/** Aviso amarillo de la vista (Instagram sin conectar, web sin publicar) con su botón si la persona puede. */
export function AvisoVista({ icono, titulo, texto, boton, tono = 'aviso' }: { icono: NombreIcono; titulo: string; texto: string; boton?: { texto: string; icono: NombreIcono; onClick: () => void } | null; tono?: 'aviso' | 'info' }) {
    return (
        <div className={`rv-ls-aviso rv-ls-aviso--${tono}`} role="status">
            <span className="rv-ls-aviso__icono"><Icono nombre={icono} /></span>
            <span className="rv-ls-aviso__texto">
                <strong>{titulo}</strong> {texto}
            </span>
            {boton != null && (
                <Boton tipo="secundario" icono={boton.icono} onClick={boton.onClick}>
                    {boton.texto}
                </Boton>
            )}
        </div>
    );
}

export function Fila({ titulo, linea, chip, derecha, inicial, baldosa, color, onAbrir, apagada = false }: { titulo: string; linea?: string; chip?: [string, EstadoChip] | null; derecha?: string; inicial?: boolean; baldosa?: NombreIcono; color?: string; onAbrir?: () => void; apagada?: boolean }) {
    const contenido = (
        <>
            {inicial === true && <span className="rv-ca-inicial rv-ca-inicial--roja" aria-hidden="true">{iniciales(titulo)}</span>}
            {baldosa !== undefined && (
                <span className="rv-ca-baldosa" style={color === undefined ? undefined : { background: color, color: '#fff' }}>
                    <Icono nombre={baldosa} />
                </span>
            )}
            <span className="rv-ca-fila__texto">
                <strong>{titulo}</strong>
                {linea !== undefined && linea !== '' && <span>{linea}</span>}
            </span>
            {chip != null && <Chip estado={chip[1]}>{chip[0]}</Chip>}
            {derecha !== undefined && <strong className="rv-ls-derecha">{derecha}</strong>}
            {onAbrir !== undefined && <Icono nombre="chevron-right" className="rv-ca-chevron" />}
        </>
    );
    return onAbrir === undefined ? (
        <div className={`rv-ls-fila${apagada ? ' rv-ls-fila--apagada' : ''}`}>{contenido}</div>
    ) : (
        <button type="button" className={`rv-ls-fila rv-ls-fila--abre${apagada ? ' rv-ls-fila--apagada' : ''}`} onClick={onAbrir}>
            {contenido}
        </button>
    );
}

